/**
 * Device-local learning state (resume points, heart timers, milestone popups)
 * used to live under global localStorage keys. On a shared device — a parent
 * handing the phone to a child, or two siblings on the same tablet — the next
 * account inherited the previous account's resume point, which made a brand
 * new child land on "Lesson 18 of 19".
 *
 * Two guarantees here:
 *  1. The resume pointer is keyed by user id, so it can never cross accounts.
 *  2. Whenever the signed-in user changes, every legacy/global progress key is
 *     purged from the device.
 */

const LAST_USER_KEY = 'sportara_last_user_id';

/** Keys (exact or prefix) that describe one user's learning state. */
const VOLATILE_PREFIXES = [
  'last_attempted_lesson_',
  'streak_milestone_',
  'hearts_last_lost_at',
  'hearts_snapshot',
  'lesson_progress_',
  'coach_assignments_seen_at',
  'sportara.unlockedSports',
  'welcome_back_',
  'weekly_recap_seen',
  'sportara-view-mode',
  'milestone_seen',
  'sportara_coach_tour',
  'coach_onboarding',
];


export function lastAttemptedLessonKey(userId: string | undefined | null, sport: string) {
  return userId ? `last_attempted_lesson_${userId}_${sport}` : `last_attempted_lesson_${sport}`;
}

/** Mid-lesson resume state (question index, hearts, partial score). */
export function lessonProgressKey(userId: string | undefined | null, lessonId: string) {
  return userId ? `lesson_progress_${userId}_${lessonId}` : `lesson_progress_${lessonId}`;
}

/** "New assignment" badge watermark. */
export function assignmentsSeenKey(userId: string | undefined | null) {
  return userId ? `coach_assignments_seen_at_${userId}` : 'coach_assignments_seen_at';
}

/** Sports the learner has unlocked on this device. */
export function unlockedSportsKey(userId: string | undefined | null) {
  return userId ? `sportara.unlockedSports_${userId}` : 'sportara.unlockedSports';
}


export function purgeDeviceProgressState() {
  try {
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && VOLATILE_PREFIXES.some(p => key.startsWith(p))) doomed.push(key);
    }
    doomed.forEach(k => localStorage.removeItem(k));
  } catch {
    /* storage unavailable — nothing to leak */
  }
}

/**
 * Call on every auth state change. Clears device-local learning state when the
 * active account differs from the one that last used this device.
 */
export function syncDeviceStateForUser(userId: string | null) {
  try {
    const previous = localStorage.getItem(LAST_USER_KEY);
    if (previous && previous !== userId) purgeDeviceProgressState();
    if (userId) localStorage.setItem(LAST_USER_KEY, userId);
    else localStorage.removeItem(LAST_USER_KEY);
  } catch {
    /* ignore */
  }
}
